import { supabase } from "@/integrations/supabase/client";

/**
 * Zoom-Links der Termine (Sicherheitscheck A4, docs/miko-labs/spaeter.md Punkt 28, Frage 238).
 *
 * `termine.anmelde_link` und `termine.meeting_link` tragen den Zoom-Link. Besucher:innen und Konten
 * dürfen diese beiden Spalten nicht lesen (Spaltenrechte). Deshalb:
 *
 * - Abfragen auf `termine` nennen ihre Spalten; `select("*")` (auch `termine(*)` in einer
 *   eingebetteten Abfrage) scheitert mit „permission denied for table termine“.
 * - Den Link gibt nur `termin_zugangslinks()`: bei einer Buchung, die nicht storniert ist, und der
 *   Administration bzw. der Kursleitung ihrer Termine. Ohne Buchung kommt für den Termin keine Zeile.
 *
 * `src/lib/termine/__tests__/zugangslinks.test.ts` hält die Spaltenliste an der Tabelle und verbietet
 * `select("*")` und die beiden Spalten in Abfragen auf `termine`.
 */

/** Alle Spalten von `termine` außer `anmelde_link` und `meeting_link`. */
export const TERMIN_SPALTEN_OHNE_LINKS =
  "id, titel, beschreibung, datum, dauer_minuten, typ, status, zugangstyp, dozent, zielgruppe, inhalte, bild_url, video_url, ausbildungs_info_text, ausbildungs_link, sonstiges_ueberschrift, sonstiges_inhalt, info_link_text, info_link_url, preis_normal, max_teilnehmer, created_by, created_at, updated_at, begleitkurs_id, stripe_price_id, stripe_product_id, welcome_email_enabled, welcome_email_subject, welcome_email_body, welcome_email_cc, ort, slug, reminder_email_enabled, reminder_email_subject, reminder_email_body, reminder_email_cc, followup_email_enabled, followup_email_subject, followup_email_body, followup_email_cc, reminder_hours_before, followup_member_enabled, followup_member_subject, followup_member_body, followup_member_cc, followup_nonmember_enabled, followup_nonmember_subject, followup_nonmember_body, followup_nonmember_cc, certificate_enabled, certificate_hours, certificate_title, landing_page_url, reihe_id, thema_id" as const;

export interface TerminZugangslinks {
  anmelde_link: string | null;
  meeting_link: string | null;
}

/** Links der Termine, die das Konto sehen darf (gebucht, Administration, Kursleitung). */
export async function zugangslinksLaden(terminIds: string[]): Promise<Map<string, TerminZugangslinks>> {
  const ids = Array.from(new Set(terminIds.filter(Boolean)));
  const ergebnis = new Map<string, TerminZugangslinks>();
  if (ids.length === 0) return ergebnis;
  const { data, error } = await supabase.rpc("termin_zugangslinks", { _termin_ids: ids });
  if (error) {
    console.error("Zoom-Links konnten nicht geladen werden:", error);
    return ergebnis;
  }
  for (const zeile of data ?? []) {
    ergebnis.set(zeile.termin_id, { anmelde_link: zeile.anmelde_link, meeting_link: zeile.meeting_link });
  }
  return ergebnis;
}

/**
 * Setzt `anmelde_link` an den Termin jeder Buchung (gewohnte Terminseite und „Meine Termine“, die sich
 * einen Cache teilen und deshalb dieselbe Form liefern müssen).
 */
export async function mitZugangslinkAnBuchungen<B extends { termin: { id: string } }>(
  buchungen: B[],
): Promise<(B & { termin: B["termin"] & { anmelde_link: string | null } })[]> {
  const links = await zugangslinksLaden(buchungen.map((b) => b.termin.id));
  return buchungen.map((b) => ({
    ...b,
    termin: { ...b.termin, anmelde_link: links.get(b.termin.id)?.anmelde_link ?? null },
  }));
}

/** Setzt `anmelde_link` und `meeting_link` an jeden Termin (ohne Recht: `null`). */
export async function mitZugangslinks<T extends { id: string }>(
  termine: T[],
): Promise<(T & TerminZugangslinks)[]> {
  const links = await zugangslinksLaden(termine.map((t) => t.id));
  return termine.map((t) => ({ ...t, anmelde_link: null, meeting_link: null, ...links.get(t.id) }));
}
